/**
 * §11.3 flash-threshold envelope follower + hard rate limiter.
 *
 * Spec (docs/design-system/plate-and-hairline/11-Accessibility.dc.html):
 *   1. Envelope-following, not transient-following: 120ms attack / 320ms release.
 *   2. Hard rate limiter: at most 3 direction changes per second, measured over
 *      a rolling 1s window. Beyond that, the fill HOLDS its current level until
 *      the window clears (this is the part the spec's "first draft" shipped
 *      without — the envelope alone still measures ~9.0 Hz on a percussive
 *      2s clip, indistinguishable from the rejected transient curve).
 *   3. No single step may cross more than 40% of the glyph's luminance range.
 *
 * This module is the single source of truth for that algorithm so the mobile
 * player's plate mask (§03.5) and any other signal-in-type surface stay
 * numerically identical to the spec's flashProof().
 */

export const ENVELOPE_ATTACK_MS = 120;
export const ENVELOPE_RELEASE_MS = 320;
export const FLASH_HOLD_MS = 333; // yields the spec's "3 changes / rolling 1s" limit
export const FLASH_STEP_CAP = 0.4; // 40% of luminance range per step

// Analyser RMS values are linear amplitude, while the useful range of a
// sound is perceived logarithmically. Multiplying RMS by a small constant
// left ordinary hero audio stuck near the baseline (the glyph never reached
// its full-height peak). Map the measured RMS in dBFS first so quiet material
// settles near zero and a healthy audio peak can occupy the whole glyph.
export const RMS_DISPLAY_FLOOR_DB = -42;
export const RMS_DISPLAY_CEILING_DB = -22;

/** Convert a linear RMS sample into the 0..1 visual level used by the mask. */
export function normalizeRmsLevel(rms: number): number {
  if (!Number.isFinite(rms) || rms <= 0) return 0;

  const db = 20 * Math.log10(rms);
  const normalized =
    (db - RMS_DISPLAY_FLOOR_DB) /
    (RMS_DISPLAY_CEILING_DB - RMS_DISPLAY_FLOOR_DB);

  return Math.max(0, Math.min(1, normalized));
}

/**
 * Stateful envelope + flash-limiter follower. Call `push(level, dtMs)` once per
 * animation frame (or per analyser read) with a 0..1 instantaneous level; it
 * returns the limited 0..1 fill value to paint.
 */
export class EnvelopeFollower {
  private last = 0;
  private dir = 0;
  private sinceMs = Number.POSITIVE_INFINITY;

  push(level: number, dtMs: number): number {
    const clamped = Math.max(0, Math.min(1, level));
    const aAttack = 1 - Math.exp(-dtMs / ENVELOPE_ATTACK_MS);
    const aRelease = 1 - Math.exp(-dtMs / ENVELOPE_RELEASE_MS);
    const raw =
      clamped > this.last
        ? this.last + (clamped - this.last) * aAttack
        : this.last + (clamped - this.last) * aRelease;

    const d = Math.sign(Number((raw - this.last).toFixed(4)));
    let target = raw;
    if (d !== 0 && d !== this.dir && this.sinceMs < FLASH_HOLD_MS) {
      // Rate limiter: a direction change inside the hold window is refused —
      // the fill holds its current level until the rolling window clears.
      target = this.last;
    } else if (d !== 0 && d !== this.dir) {
      this.dir = d;
      this.sinceMs = 0;
    }
    this.sinceMs += dtMs;

    const step = Math.max(-FLASH_STEP_CAP, Math.min(FLASH_STEP_CAP, target - this.last));
    this.last = Math.max(0, Math.min(1, this.last + step));
    return this.last;
  }

  reset(value = 0): void {
    this.last = Math.max(0, Math.min(1, value));
    this.dir = 0;
    this.sinceMs = Number.POSITIVE_INFINITY;
  }

  get value(): number {
    return this.last;
  }
}

/** Counts luminance direction changes in a value series, spec-identical to flashCount(). */
export function countDirectionChanges(series: number[]): number {
  let count = 0;
  let prevDir = 0;
  for (let i = 1; i < series.length; i++) {
    const d = Math.sign(Number((series[i] - series[i - 1]).toFixed(4)));
    if (d !== 0 && d !== prevDir) {
      if (prevDir !== 0) count++;
      prevDir = d;
    }
  }
  return count;
}

/** Direction-change rate in Hz over a series spanning `durationSec` seconds. */
export function flashRateHz(series: number[], durationSec: number): number {
  if (durationSec <= 0) return 0;
  return countDirectionChanges(series) / durationSec / 2;
}
